import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { getCrewFeed, getIncomingRequests } from "@/api/crew";

// Backgrounded/minimized tabs must not keep polling — a tab left open across
// devices was found to keep the Cloud Run backend continuously warm, which
// is billed even while idle (see infra/modules/cloud-run/main.tf cpu_idle).
const POLL_INTERVAL_MS = 5 * 60_000;

export function useCrewBadges() {
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasUnreadComments, setHasUnreadComments] = useState(false);

  useEffect(() => {
    const fetchRequests = () =>
      getIncomingRequests()
        .then((r) => setPendingRequests(r.length))
        .catch((err) => Sentry.captureException(err));

    let interval: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (interval) return;
      fetchRequests();
      interval = setInterval(fetchRequests, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (!interval) return;
      clearInterval(interval);
      interval = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    getCrewFeed()
      .then((feed) => setHasUnreadComments(feed.some((item) => item.unread_comments > 0)))
      .catch((err) => Sentry.captureException(err));
  }, []);

  return { pendingRequests, hasUnreadComments };
}
